import {useContext, useEffect, useRef, useState} from "react";
import {MapViewContext} from "../contexts/MapViewContext";
import "./SearchFilter.css";
import {TagCountsGrouped} from "../models/TagCountsGrouped";

interface SearchFilterProps {
    categoryCounts: TagCountsGrouped;
}

const SEARCH_LIMIT = 15;
const SearchFilter = ({categoryCounts}: SearchFilterProps) => {
    const {
        searchQuery,
        setSearchQuery,
        setSelectedTagIds,
        setSelectedCategoryIds,
    } = useContext(MapViewContext);
    const [inputValue, setInputValue] = useState("");
    const [filteredResults, setFilteredResults] = useState<
        { type: "category" | "tag"; id: string; name: string; color?: string }[]
    >([]);
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const initialSearch = params.get("search") || "";
        setInputValue(initialSearch);
        setSearchQuery(initialSearch);
    }, [setSearchQuery]);

    useEffect(() => {
        setInputValue(searchQuery || "");
    }, [searchQuery]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setIsDropdownVisible(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value.trim());

        const url = new URL(window.location.href);
        if (value) {
            url.searchParams.set("search", value);
        } else {
            url.searchParams.delete("search");
        }
        window.history.replaceState(null, "", url.toString());

        if (value.length >= 2 || value.length === 0) {
            setSearchQuery(value);
            setIsDropdownVisible(true);
        }
    };

    useEffect(() => {
        const query = inputValue.toLowerCase().trim();
        if (!query) {
            setFilteredResults([]);
            return;
        }

        const results: { type: "category" | "tag"; id: string; name: string; color?: string }[] = [];

        Object.entries(categoryCounts).forEach(([catId, catData]) => {
            // Match category
            if (catData.name.toLowerCase().includes(query)) {
                results.push({type: "category", id: catId, name: catData.name});
            }
            // Match tags
            Object.entries(catData.tags).forEach(([tagId, tagData]) => {
                if (tagData.name.toLowerCase().includes(query)) {
                    results.push({
                        type: "tag",
                        id: tagId,
                        name: tagData.name,
                        color: tagData.color,
                    });
                }
            });
        });

        setFilteredResults(results.slice(0, SEARCH_LIMIT));
    }, [inputValue, categoryCounts]);

    const handleSelect = (id: string, type: "category" | "tag") => {
        setInputValue("");
        setSearchQuery("");
        setIsDropdownVisible(false);

        const url = new URL(window.location.href);

        if (type === "category") {
            const current = url.searchParams.get("categories_ids")?.split(",").filter(Boolean) || [];
            const newSelected = current.includes(id)
                ? current.filter((c) => c !== id)
                : [...current, id];

            if (newSelected.length > 0) {
                url.searchParams.set("categories_ids", newSelected.join(","));
            } else {
                url.searchParams.delete("categories_ids");
            }

            window.history.replaceState(null, "", url.toString());
            setSelectedCategoryIds(newSelected);
        }

        if (type === "tag") {
            const current = url.searchParams.get("tag_ids")?.split(",").filter(Boolean) || [];
            const newSelected = current.includes(id)
                ? current.filter((t) => t !== id)
                : [...current, id];

            if (newSelected.length > 0) {
                url.searchParams.set("tag_ids", newSelected.join(","));
            } else {
                url.searchParams.delete("tag_ids");
            }

            window.history.replaceState(null, "", url.toString());
            setSelectedTagIds(newSelected);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            setIsDropdownVisible(false);
        }
        if (e.key === "Escape") {
            e.preventDefault();
            setInputValue("");
            setSearchQuery("");
            setFilteredResults([]);
            setIsDropdownVisible(false);

            const url = new URL(window.location.href);
            url.searchParams.delete("search");
            window.history.replaceState(null, "", url.toString());
        }
    };



    return (
        <div className="search-filter" ref={wrapperRef}>
            <div className="search-bar relative">
                <input
                    type="text"
                    placeholder="Search places"
                    value={inputValue}
                    onChange={handleSearchChange}
                    onFocus={() => filteredResults.length > 0 && setIsDropdownVisible(true)}
                    onKeyDown={handleKeyDown}
                    className="search-input"
                />
                {isDropdownVisible && filteredResults.length > 0 && (
                    <ul className="search-dropdown">
                        {filteredResults.map((item) => (
                            <li
                                key={`${item.type}-${item.id}`}
                                className="dropdown-item"
                                onClick={() => handleSelect(item.id, item.type)}
                            >
                                {item.type === "tag" && (
                                    <span
                                        className="tag-color"
                                        style={{backgroundColor: item.color}}
                                    />
                                )}
                                {item.name}

                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default SearchFilter;
